/******************************************************************************

Copyright (c) 2016-2018, Highsoft

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY
CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

******************************************************************************/

// @format

//////////////////////////////////////////////////////////////////////////////

/** The main highcharts editor namespace
 * @ignore
 */

import highed from './../globals';
import { cr, ap, setStyle } from './../core/highed.dom';
import Events from './highed.events';

highed.isOnPhone = checkIfPhone();
highed.isOnTablet = checkIfTabletDimensions();

/** Trigger file download
 *  @namespace highed
 *  @param filename {string} - the filename
 *  @param data {string} - the contained data
 */
export function download(filename, data, mime) {
  let l = cr('a');
  mime = mime || 'application/octet-stream';
  l.download = filename || 'unkown';
  l.href = 'data:' + mime + ',' + encodeURIComponent(data);
  ap(document.body, l);
  l.click();
  document.body.removeChild(l);
}

/** Clear an object
 * Deletes all the object attributes.
 * Useful when needing to clear an object without invalidating references to it
 * @namespace highed
 * @param obj {object} - the object to clear
 */
export function clearObj(obj) {
  Object.keys(obj).forEach(function (key) {
    delete obj[key];
  });
}

/** Preform an AJAX request. Same syntax as jQuery.
 *  @namespace highed
 *  @param p {object} - options
 *    > url {string} - the URL to call
 *    > type {enum} - the type of request
 *    > dataType {enum} - the type of data expected
 *    > success {function} - function to call on success
 *    > error {function} - function to call on request fail
 *    > data {object} - the payload
 *    > autoFire {boolean} - whether or not to fire the request right away
 *
 *   @emits Error {string} - when there's an error
 *   @emits OK {string} - when the request succeeded
 *   @returns {object} - interface to the request
 */
export function ajax(p) {
  let props = merge(
      {
        url: false,
        type: 'GET',
        dataType: 'json',
        success: false,
        error: false,
        data: {},
        autoFire: true,
        headers: {}
      },
      p
    ),
    headers = {
      json: 'application/json',
      xml: 'application/xml',
      text: 'text/plain',
      octet: 'application/octet-stream'
    },
    r = new XMLHttpRequest(),
    events = Events();

  if (!props.url) return false;

  r.open(props.type, props.url, true);

  if (!props.skipContentType) r.setRequestHeader('Content-Type', headers[props.dataType] || headers.text);

  Object.keys(props.headers).forEach(function (key) {
    r.setRequestHeader(key, props.headers[key]);
  });

  r.onreadystatechange = function () {
    events.emit('ReadyStateChange', r.readyState, r.status);

    if (r.readyState === 4 && r.status === 200) {
      if (props.dataType === 'json') {
        try {
          let json = JSON.parse(r.responseText);
          if (isFn(props.success)) {
            props.success(json);
          }
          events.emit('OK', json);
        } catch (e) {
          console.log('parse error', e);
          if (isFn(props.error)) {
            props.error(e.toString(), r.responseText);
          }
          events.emit('Error', e.toString(), r.status);
        }
      } else {
        if (isFn(props.success)) {
          props.success(r.responseText);
        }
        events.emit('OK', r.responseText);
      }
    } else if (r.readyState === 4) {
      events.emit('Error', r.status, r.statusText);
      if (isFn(props.error)) {
        props.error(r.status, r.statusText);
      }
    }
  };

  function fire() {
    try {
      r.send(JSON.stringify(props.data));
    } catch (e) {
      r.send(props.data || true);
    }
  }

  if (props.autoFire) {
    fire();
  }

  return {
    on: events.on,
    fire: fire,
    request: r
  };
}

/** Generate a uuid
 *  Borrowed from http://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript
 *  @namespace highed
 *  @returns {string} - a UUID string
 */
export function uuid() {
  let d = new Date().getTime(),
    uuid;

  if (window.performance && typeof window.performance.now === 'function') {
    d += window.performance.now(); //use high-precision timer if available
  }

  uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    let r = (d + Math.random() * 16) % 16 | 0;
    d = Math.floor(d / 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
  return uuid;
}

/** Map an array to an object
 *  @namespace highed
 *  @param {array} arr - the array to map
 *  @return {object} - an object with the array contents as keys, and their value set to true
 */
export function arrToObj(arr) {
  let obj = {};

  if ((!isArr(arr) && !isBasic(arr)) || arr === false) {
    return arr;
  }

  if (isStr(arr)) {
    arr = arr.split(' ');
  }

  arr.forEach(function (thing) {
    obj[thing] = true;
  });

  return obj;
}

/** Make a camel back string pretty
 *  Transforms e.g. `imACamelBackString` to `Im a camelback string`.
 *  @namespace highed
 *  @param str {string} - the input string
 *  @return {string} - the transformed string
 */
export function uncamelize(str) {
  let s = '';

  if (!str) {
    return str;
  }

  if (str.length < 0 || !str) {
    return str;
  }

  for (let i = 0; i < str.length; i++) {
    if (str[i] === str[i].toUpperCase()) {
      if (
        (str[i + 1] && str[i + 1] === str[i + 1].toUpperCase()) ||
        (str[i - 1] && str[i - 1] === str[i - 1].toUpperCase())
      ) {
      } else {
        s += ' ';
      }
    }
    s += str[i];
  }

  return s[0].toUpperCase() + s.substr(1);
}

/** Clamp a number between min/max
 *  @namespace highed
 *  @param min {number} - minimum value
 *  @param max {number} - maximum value
 *  @param value {number} - the value to clamp
 *  @returns {number} - the clamped value
 */
export function clamp(min, max, value) {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

/** Convert a hex value to RGB
 *
 *  @namespace highed
 *  @param {string} hex - the hex string
 *  @return {object} - an object with rgb components
 *    > r {number} - red
 *    > g {number} - green
 *    > b {number} - blue
 */
export function hexToRgb(h) {
  let r = 0,
    g = 0,
    b = 0;

  // 3 digits
  if (h.length == 4) {
    r = '0x' + h[1] + h[1];
    g = '0x' + h[2] + h[2];
    b = '0x' + h[3] + h[3];

    // 6 digits
  } else if (h.length == 7) {
    r = '0x' + h[1] + h[2];
    g = '0x' + h[3] + h[4];
    b = '0x' + h[5] + h[6];
  }

  return {
    r: parseInt(r),
    g: parseInt(g),
    b: parseInt(b)
  };
}

/** Convert a hex value to RGBA
 *
 *  @namespace highed
 *  @param {string} hex - the hex string
 *  @return {object} - an object with rgba components
 *    > r {number} - red
 *    > g {number} - green
 *    > b {number} - blue
 *    > a {number} - alpha
 */
export function hexToRgba(h) {
  let r = 0,
    g = 0,
    b = 0,
    a = 255;

  if (h.length == 5) {
    r = '0x' + h[1] + h[1];
    g = '0x' + h[2] + h[2];
    b = '0x' + h[3] + h[3];
    a = '0x' + h[4] + h[4];
  } else if (h.length == 9) {
    r = '0x' + h[1] + h[2];
    g = '0x' + h[3] + h[4];
    b = '0x' + h[5] + h[6];
    a = '0x' + h[7] + h[8];
  }

  return {
    r: parseInt(r),
    g: parseInt(g),
    b: parseInt(b),
    a: parseInt(a)
  };
}

/** Invert a color
 *  @namespace highed
 *  @param {string} hex - the color to invert
 *  @return {string} - new hex color
 */
export function invertHexColor(hex) {
  let rgb = hexToRgb(hex),
    res = 0;

  rgb.r = 255 - rgb.r;
  rgb.g = 255 - rgb.g;
  rgb.b = 255 - rgb.b;

  res = rgb.r << 16;
  res |= rgb.g << 8;
  res |= rgb.b;

  return '#' + res;
}

/** Return #FFF or #000 based on the intensity of a color
 *  @namespace highed
 *  @param {string} hex - input color
 *  @return {string} - the new hex color
 */
export function getContrastedColor(hex) {
  let rgb = hexToRgb(hex),
    avarage = (rgb.r + rgb.g + rgb.b) / 3;

  if (avarage > 150) {
    return '#000';
  }
  return '#FFF';
}

/** Convert a string to a bool
 *  @namespace highed
 *  @param {string} what - the string to convert
 *  @return {bool} - the resulting bool
 */
export function toBool(what) {
  return what === 'true' || what === true || what === 'on';
}

/** Set a property based on -- delimited path
 *  @namespace highed
 *  @param {object} obj - the object to modify
 *  @param {string} path - the path to the attribute to change
 *  @param {anything} value - the value to set
 *  @param {number} index - if we're accessing an array, this is the index
 */
export function setAttr(obj, path, value, index) {
  let current = obj;

  if (!current) return;

  if (isArr(obj)) {
    obj.forEach(function (thing) {
      setAttr(thing, path, value, index);
    });
    return;
  }

  path = path.replace(/\-\-/g, '.').replace(/\-/g, '.').split('.');

  path.forEach(function (p, i) {
    if (i === path.length - 1) {
      current[p] = value;
    } else {
      if (typeof current[p] === 'undefined') {
        current = current[p] = {};
      } else {
        current = current[p];

        if (isArr(current)) {
          if (index > current.length - 1) {
            for (let j = current.length; j <= index; j++) {
              current.push({});
            }
          }
          if (index >= 0) {
            current = current[index];
          }
        }
      }
    }
  });
}

/** Get a property based on -- delimited path
 *  @namespace highed
 *  @param {object} obj - the object to traverse
 *  @param {string} path - the path to the attribute to get
 *  @param {number} index - if we're accessing an array, this is the index
 *  @returns {anything} - the value or false
 */

export function getAttr(obj, path, index) {
  let current = obj,
    result = undefined;

  if (!current) return result;

  if (isArr(obj)) {
    obj.forEach(function (thing) {
      result = getAttr(thing, path);
    });
    return result;
  }

  path = path.replace(/\-\-/g, '.').replace(/\-/g, '.').split('.');

  path.forEach(function (p, i) {
    if (i === path.length - 1) {
      if (typeof current !== 'undefined') {
        result = current[p];
      }
    } else {
      if (typeof current[p] === 'undefined') {
        current = current[p] = {};
      } else {
        current = current[p];

        if (isArr(current) && index >= 0 && index < current.length) {
          current = current[index];
        }
      }
    }
  });

  return result;
}

export function isEmptyObjectArray(arr) {
  return (
    isObj(arr[0]) &&
    arr.some(function (b) {
      return Object.keys(b).length === 0;
    })
  );
}

export function isObj(what) {
  return what && what.constructor.toString().indexOf('Object') > -1;
}

/** Deep merge two objects.
 * Note: this modifies object `a`!
 * @namespace highed
 * @param {object} a - the destination object
 * @param {object} b - the source object
 * @param {bool} ignoreEmpty - Ignore empty things
 * @param {object} excludeMap - Map of properties to exclude from the merge
 * @return {object} - argument a
 */
export function merge(a, b, ignoreEmpty, excludeMap) {
  if (!a || !b) return a || b;

  if (ignoreEmpty && Object.keys(b).length === 0) {
    return;
  }

  Object.keys(b).forEach(function (bk) {
    if (excludeMap && excludeMap[bk]) {
    } else if (isNull(b[bk]) || isBasic(b[bk])) {
      a[bk] = b[bk];
    } else if (isArr(b[bk])) {
      if (isEmptyObjectArray(b[bk])) return;
      a[bk] = [];

      b[bk].forEach(function (i) {
        if (isNull(i) || isBasic(i)) {
          a[bk].push(i);
        } else {
          a[bk].push(merge(isArr(i) ? [] : {}, i));
        }
      });
    } else if (b[bk].tagName && b[bk].appendChild && b[bk].removeChild && b[bk].style) {
      a[bk] = b[bk];
    } else {
      if (ignoreEmpty && Object.keys(b[bk]).length === 0) {
        return;
      }

      a[bk] = a[bk] || {};
      merge(a[bk], b[bk]);
    }
  });
  return a;
}

/** Check if something is null or undefined
 *  @namespace highed
 *  @param {anything} what - the value to check
 *  @return {bool} - true if null
 */
export function isNull(what) {
  return typeof what === 'undefined' || what === null;
}

/** Check if something is a string
 *  @namespace highed
 *  @param {anything} what - the value to check
 *  @return {bool} - true if string
 */
export function isStr(what) {
  return typeof what === 'string' || what instanceof String;
}

/** Check if something is a number
 * @namespace highed
 *  @param {anything} what - the value to check
 *  @return {bool} - true if number
 */
export function isNum(what) {
  return !isNaN(parseFloat(what)) && isFinite(what);
}

export function getNextLetter(key) {
  if (key === 'Z' || key === 'z') {
    return String.fromCharCode(key.charCodeAt() - 25) + String.fromCharCode(key.charCodeAt() - 25);
  } else {
    let lastChar = key.slice(-1);
    let sub = key.slice(0, -1);
    if (lastChar === 'Z' || lastChar === 'z') {
      return getNextLetter(sub) + String.fromCharCode(lastChar.charCodeAt() - 25);
    } else {
      return sub + String.fromCharCode(lastChar.charCodeAt() + 1);
    }
  }
  return key;
}

/** Check if a value is a function
 * @namespace highed
 * @param {anything} what - the value to check
 * @return {bool} - true if function
 */
export function isFn(what) {
  return (what && typeof what === 'function') || what instanceof Function;
}

/** Check if a value is a function
 * @return {bool} - if using safari
 */
export function usingSafari() {
  return (
    navigator.userAgent.indexOf('Safari') != -1 &&
    navigator.userAgent.indexOf('Mac') != -1 &&
    navigator.userAgent.indexOf('Chrome') == -1
  );
}

/** Check if a value is an array
 * @namespace highed
 * @param {anything} what - the value to check
 * @return {bool} - true if array
 */
export function isArr(what) {
  return !isNull(what) && what.constructor.toString().indexOf('Array') > -1;
}

/** Check if a value is a boolean
 * @namespace highed
 * @param {anything} what - the value to check
 * @return {bool} - true if bool
 */
export function isBool(what) {
  return what === true || what === false;
}

/** Check if a value is a basic type
 * A basic type is either a bool, string, or a number
 * @namespace highed
 * @param {anything} what - the value to check
 * @return {bool} - true if basic
 */
export function isBasic(what) {
  return !isArr(what) && (isStr(what) || isNum(what) || isBool(what) || isFn(what));
}

export function copyTextToClipboard(text) {
  let textArea = document.createElement('textarea');
  textArea.value = text;
  document.body.appendChild(textArea);
  textArea.select();

  try {
    let successful = document.execCommand('copy');
    document.body.removeChild(textArea);
  } catch (err) {
    document.body.removeChild(textArea);
  }
}

export function detectLeftButton(evt) {
  evt = evt || window.event;
  if ('buttons' in evt) {
    return evt.buttons == 1;
  }
  var button = evt.which || evt.button;
  return button == 1;
}

/*
 ** Returns the caret (cursor) position of the specified text field (oField).
 ** Return value range is 0-oField.value.length.
 */
export function getCaretPosition(oField) {
  // Initialize
  let iCaretPos = 0;

  // IE Support
  if (document.selection) {
    // Set focus on the element
    oField.focus();

    // To get cursor position, get empty selection range
    let oSel = document.selection.createRange();

    // Move selection start to 0 position
    oSel.moveStart('character', -oField.value.length);

    // The caret position is selection length
    iCaretPos = oSel.text.length;
  }

  // Firefox support
  else if (oField.selectionStart || oField.selectionStart == '0')
    iCaretPos = oField.selectionDirection == 'backward' ? oField.selectionStart : oField.selectionEnd;

  // Return results
  return iCaretPos;
}

function guessDelimiter(lines) {
  var points = 0,
    commas = 0,
    guessed = false;

  const potDelimiters = {
    ',': true,
    ';': true,
    '\t': true
  };
  const delimiterCounts = {
    ',': 0,
    ';': 0,
    '\t': 0
  };

  lines.some(function (columnStr, i) {
    var inStr = false,
      c,
      cn,
      cl,
      token = '';
    // We should be able to detect dateformats within 13 rows
    if (i > 10) {
      return true;
    }
    for (var j = 0; j < columnStr.length; j++) {
      c = columnStr[j];
      cn = columnStr[j + 1];
      cl = columnStr[j - 1];
      if (c === '#') {
        // Skip the rest of the line - it's a comment
        return;
      }
      if (c === '"') {
        if (inStr) {
          if (cl !== '"' && cn !== '"') {
            while (cn === ' ' && j < columnStr.length) {
              cn = columnStr[++j];
            }
            // After parsing a string, the next non-blank
            // should be a delimiter if the CSV is properly
            // formed.
            if (typeof potDelimiters[cn] !== 'undefined') {
              potDelimiters[cn]++;
            }
            inStr = false;
          }
        } else {
          inStr = true;
        }
      } else if (!inStr && typeof potDelimiters[c] !== 'undefined') {
        token = token.trim();
        if (!isNaN(Date.parse(token))) {
          potDelimiters[c]++;
        } else if (isNaN(token) || !isFinite(token) || /^\d+$/.test(token)) {
          potDelimiters[c]++;
        }
        token = '';
      } else {
        token += c;
      }
      if (c === ',') {
        commas++;
      }
      if (c === '.') {
        points++;
      }
    }
  });
  // Count the potential delimiters.
  // This could be improved by checking if the number of delimiters
  // equals the number of columns - 1

  if (potDelimiters[','] > potDelimiters[';'] && potDelimiters[','] > potDelimiters['\t']) {
    guessed = ',';
  } else if (potDelimiters['\t'] >= potDelimiters[';'] && potDelimiters['\t'] >= potDelimiters[',']) {
    guessed = '\t';
  } else {
    guessed = ';';
  }

  // Try to deduce the decimal point if it's not explicitly set.
  // If both commas or points is > 0 there is likely an issue
  /*
   if (!options.decimalPoint) {
       if (points > commas) {
           options.decimalPoint = '.';
       }
       else {
           options.decimalPoint = ',';
       }
       // Apply a new decimal regex based on the presumed decimal sep.
       self.decimalRegex = new RegExp('^(-?[0-9]+)' +
           options.decimalPoint +
           '([0-9]+)$');
   }*/
  return guessed;
}

/** Parse CSV Data
 * @namespace highed
 * @param {string} - inData
 * @return {string} - delimiter
 */

export function parseCSV(inData, delimiter, section) {
  let csv = inData || '',
    result = [],
    options = {
      delimiter: delimiter
    },
    rows;
  //The only thing CSV formats have in common..
  rows = (csv || '').replace(/\r\n/g, '\n').split('\n');

  // If there's no delimiter, look at the first few rows to guess it.
  if (!options.delimiter) {
    options.delimiter = guessDelimiter(rows);
  }

  rows.forEach(function (row, rowNumber) {
    let cols = [],
      columnCount,
      inStr = false,
      i = 0,
      j,
      token = '',
      guessedDel,
      c,
      cp,
      cn;

    function pushToken() {
      columnCount = columnCount + 1 || 0;
      //Dont know why this line is even in here tbh
      //token = (token || '').replace(/\,/g, '');
      if (!token.length) {
        token = null;
        // return;
      }

      if (isNum(token)) {
        token = parseFloat(token);
      }

      if (section && !checkSections(section, columnCount)) {
        token = '';
        return;
      }

      cols.push(token);
      token = '';
    }

    for (i = 0; i < row.length; i++) {
      c = row[i];
      cn = row[i + 1];
      cp = row[i - 1];

      if (c === '"') {
        if (inStr) {
          inStr = false;
          //pushToken();
        } else {
          inStr = true;
        }

        //Everything is allowed inside quotes
      } else if (inStr) {
        token += c;
        //Check if we're done reading a token
      } else if (c === options.delimiter) {
        pushToken(rowNumber);

        //Append to token
      } else {
        token += c;
      }

      // Push if this was the last character
      if (i === row.length - 1) {
        pushToken(rowNumber);
      }
    }

    result.push(cols);
  });

  return result;
}

/** Convert an array back to csv
 * @namespace highed
 * @param {string} - csvArray
 * @return {string} - delimiter
 */
export function toCSV(csvArray, delimiter) {
  return csvArray
    .map(function (cols) {
      cols = cols.map((column) => (!isNum(column) && isStr(column) ? '"' + column + '"' : column));
      return cols.join(delimiter ?? ';');
    })
    .join('\n');
}

/** Convert an array back to csv for maps.
 *  Similar to above function, we also remove null values from the final result
 *  Highcharts doesnt like null rows here.
 * @namespace highed
 * @param {string} - csvArray
 * @return {string} - delimiter
 */
export function toMapCSV(csvArray, delimiter) {
  return csvArray
    .filter((cols) => cols[1] !== null && cols[1] !== undefined)
    .map((cols) => cols.join(delimiter ?? ';'))
    .join('\n');
}

export function removeNulls(dataSet) {
  const newDataArr = [];
  dataSet.forEach(function (e) {
    let rarr = [],
      hasData = false;

    e.forEach(function (v) {
      if (v) {
        hasData = true;
      }

      if (!isNum(v) && isStr(v)) {
        v = v.replace(/\"/g, '"');
      }

      if (isNum(v)) {
        v = parseFloat(v);
      }

      if (isStr(v) && Date.parse(v) !== NaN) {
        //v = (new Date(v)).getTime();
      }

      rarr.push(v);
    });

    if (hasData) {
      newDataArr.push(rarr);
    }
  });

  return newDataArr;
}

export function isScriptAlreadyIncluded(scripts, src) {
  for (let i = 0; i < scripts.length; i++) {
    if (scripts[i].hasAttribute('src')) {
      const scriptTag = scripts[i].getAttribute('src') || '';
      if (
        scriptTag.indexOf(src) >= 0 ||
        (scriptTag.indexOf('highcharts.src.js') > -1 && src === 'highcharts.js') ||
        (scriptTag.indexOf('stock/highstock.js') > -1 && src === 'modules/highstock.js')
      ) {
        return true;
      }
    }
  }
  return false;
}

export function loadFont(fontUrl, cb) {
  let s = document.createElement('link');
  s.rel = 'stylesheet';
  s.async = true;
  s.href = fontUrl;

  s.onload = s.onreadystatechange = function (_, isAbort) {
    if (isAbort || !s.readyState || /loaded|complete/.test(s.readyState)) {
      s.onload = s.onreadystatechange = null;
      s = undefined;

      if (!isAbort) setTimeout(cb, 500);
    }
  };
  document.getElementsByTagName('head')[0].appendChild(s);
}

export function loadModules(paths, isJS, cb, addToExportList) {
  const length = paths.length;
  let counter = 0;

  paths.forEach(function (path) {
    let s;

    if (isJS) {
      let scripts = document.getElementsByTagName('script');
      if (!isScriptAlreadyIncluded(scripts, path)) {
        s = document.createElement('script');
        s.type = 'text/javascript';
        s.src = path;
        s.onload = s.onreadystatechange = function (_, isAbort) {
          if (isAbort || !s.readyState || /loaded|complete/.test(s.readyState)) {
            s.onload = s.onreadystatechange = null;
            s = undefined;
            counter++;
            if (counter === length - 1) {
              if (cb) cb();
            }
          }
        };
        document.getElementsByTagName('head')[0].appendChild(s);
      }
    } else {
      s = document.createElement('link');
      s.rel = 'stylesheet';
      s.href = path;
      s.async = true;
      document.getElementsByTagName('head')[0].appendChild(s);
      if (addToExportList) {
        if (!window.Everviz) window.Everviz = {};
        if (!window.Everviz.externalCSS) window.Everviz.externalCSS = [];
        if (window.Everviz.externalCSS.indexOf(path) === -1) {
          window.Everviz.externalCSS.push(path);
        }
      }
    }
  });
}

export function keyifyNestedObject(object) {
  function iter(o, p) {
    //if (Array.isArray(o)) { return; }
    if (o && typeof o === 'object') {
      let keys = Object.keys(o);
      if (keys.length) {
        keys.forEach(function (k) {
          iter(o[k], p.concat(k));
        });
      }
      return;
    }
    result.push(p.join('--'));
  }
  let result = [];
  iter(object, []);
  return result;
}

export function getObjectValueByString(o, s) {
  s = s.replace(/\[(\w+)\]/g, '--$1');
  s = s.replace(/^\--/, '');
  let a = s.split('--');
  for (let i = 0, n = a.length; i < n; ++i) {
    let k = a[i];
    if (k in o) {
      o = o[k];
    } else {
      return;
    }
  }
  return o;
}

export function removeTextFields(o, key, parent) {
  if (isArr(o)) {
    return o.forEach(function (k, index) {
      return isObj(k) ? removeTextFields(k, index, o) : null;
    });
  } else if (isObj(o)) {
    return Object.keys(o).forEach(function (k) {
      return removeTextFields(o[k], k, o);
    });
  } else {
    if (key === 'text') {
      delete parent[key];
    }
  }
}

export function clean(obj) {
  Object.keys(obj).forEach((key) => {
    if (isNull(obj[key])) delete obj[key];
  });
  return obj;
}

export function generateRandomColors() {
  let randomRGB = getRandomRgb();
  return {
    light: 'rgb(' + generateLighterColor(randomRGB, 0.2) + ')',
    dark: 'rgb(' + randomRGB.join(',') + ')'
  };
}

export function getLetterIndex(letters) {
  return letters.split('').reduce((r, a) => r * 26 + parseInt(a, 36) - 9, 0) - 1;
}

export function getLetterFromIndex(i) {
  return (i >= 26 ? getLetterFromIndex(Math.floor(i / 26) - 1) : '') + 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[i % 26];
}

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function getAllMergedLabelAndData(options) {
  let seriesValues = [];
  options.forEach(function (serie, i) {
    let arr = {},
      extraColumns = [],
      values = [];
    Object.keys(serie).forEach(function (optionKeys) {
      if (optionKeys === 'labels') {
        arr.labelColumn = getLetterIndex(options[i][optionKeys]);
      } else if (optionKeys === 'values') {
        const allData = options[i][optionKeys];
        values.push(getLetterIndex(allData));
        arr.dataColumns = values;
        arr.dataColumns.sort();
      } else {
        const extraValue = options[i][optionKeys];
        if (extraValue !== '') {
          extraColumns.push(getLetterIndex(extraValue));
        }
      }
    });
    arr.extraColumns = extraColumns.sort();
    seriesValues.push(merge({}, arr));
  });
  return seriesValues;
}

export function checkSections(sections, index) {
  return (sections || []).some(function (section) {
    return (
      (section.dataColumns && section.dataColumns.includes(index)) ||
      (section.extraColumns && section.extraColumns.includes(index)) ||
      section.labelColumn === index
    );
  });
}

export function createWordMap(arr) {
  let obj = {};
  arr.forEach(function (el) {
    obj[el] = obj[el] ? ++obj[el] : 1;
  });
  return obj;
}

export function sortObjectByCount(wordsMap) {
  let finalWordsArray = [];
  finalWordsArray = Object.keys(wordsMap).map(function (key) {
    return {
      name: key,
      total: wordsMap[key]
    };
  });

  finalWordsArray.sort(function (a, b) {
    return b.total - a.total;
  });
  return finalWordsArray;
}

export function getCSVFromText(text, maxAmount) {
  text = text.replace(/(?:\r\n|\r|\n)/g, ' ').replace(/\'|"/g, '');
  let lines = text.split(/[,\. ]+/g),
    occurances = createWordMap(lines);

  occurances = sortObjectByCount(occurances, maxAmount);
  if (isNum(maxAmount)) {
    occurances = occurances.slice(0, maxAmount);
  }

  let data = occurances.map(function (obj) {
    return obj.name + ',' + obj.total;
  });

  data.unshift(['Name,Weight']);

  return data.join('\n');
}

///////////////////////////////////////////////////////////////////////////

export function pollForReady() {
  if (!highed.isReady) {
    if (document.body) {
      highed.isReady = true;
      highed.initQueue.forEach(function (fn) {
        fn();
      });
    } else {
      window.setTimeout(pollForReady, 100);
    }
  }
}

pollForReady();

///////////////////////////////////////////////////////////////////////////

/**
 * Whitelist an option in simple view
 */
export function exposeOption(option) {}

/** Set/get an option
 *  Skip `value` to get the value
 *  @param option {string} - the option to set
 *  @param value {anything} - the value to set
 *  @returns {anything} - the option value
 */
export function option(option, value) {
  if (!isBasic(option)) {
    merge(highed.options, option);
  } else if (highed.options[option]) {
    if (typeof value !== 'undefined') {
      highed.options[option] = value;
    }
    return highed.options[option];
  }
  return false;
}

/** Set a set of options
 *  @param options {object} - an object of options to set
 */
export function options(options) {
  Object.keys(options || {}).forEach(function (key) {
    option(key, options[key]);
  });
}

/** Serialize the global options
 *  @returns {object} - a copy of the global options
 */
export function serializeEditorOptions() {
  return merge({}, options);
}

/** Add a function to call when the document is ready
 * @param {function} fn - the function to call
 */
export function ready(fn) {
  if (isFn(fn)) {
    if (highed.isReady) {
      fn();
    } else {
      highed.initQueue.push(fn);
    }
  }
}

/** Log something
 * Accepts a variable amount of arguments after `level` which will be
 * the log message (similar to `console.log`).
 * @param {number} level - the log level 1..4
 */
export function log(level) {
  let things = Array.prototype.slice.call(arguments);
  things.splice(0, 1);

  if (level <= highed.currentLogLevel) {
    console.log.apply(undefined, [logLevels[level - 1] + ':'].concat(things)); //eslint-disable-line no-console
  }
}

/** Set the current log level
 *  @param level {number} - the current log level
 */
export function setLogLevel(level) {
  if (level <= highed.logLevels.length) {
    highed.currentLogLevel = level;
  }
}

/** Include something
 *  @namespace highed
 *  @param what {string} - URL to a css or javascript file
 *  @param fn {function} - function to call when done including the script
 *  @param asCSS {boolean} - force including as css
 */
export function include(what, fn, asCSS) {
  let n;

  if (!isStr(what)) {
    return isFn(fn) && fn();
  }

  function next() {
    if (n < what.length - 1) {
      include(what[++n], next);
    }

    return isFn(fn) && fn();
  }

  if (isArr(what)) {
    n = -1;
    return next();
  }

  if (includedScripts[what]) {
    log(3, 'script already included, skipping:', what);
    return fn();
  }

  log(3, 'including script', what);
  includedScripts[what] = true;

  if (asCSS || what.lastIndexOf('.css') === what.length - 4) {
    n = document.createElement('link');
    n.rel = 'stylesheet';
    n.type = 'text/css';
    n.href = what;
    n.onload = fn;
  } else {
    n = document.createElement('script');
    n.src = what;
    n.onload = fn;
  }

  document.head.appendChild(n);
}

/** Returns true if running on a phone
 *  @namespace highed
 *  @returns {boolean} - true if running on a phone
 */
export function onPhone() {
  return highed.isOnPhone;
}

/** Returns true if running on a screen size is within tablets range and checks user agent
 *  @namespace highed
 *  @returns {boolean} - true if running on a tablet
 */
export function onTablet() {
  return highed.isOnTablet;
}

export function generateColors() {
  const hue = Math.floor(Math.random() * (357 - 202 + 1)), // Want a blue/red/purple colour
    saturation = Math.floor(Math.random() * 100),
    lightness = 60,
    alpha = 0.5;

  return {
    light: 'hsl(' + hue + ', ' + saturation + '%, ' + (lightness + 20) + '%)',
    dark: 'hsl(' + hue + ', ' + saturation + '%, ' + lightness + '%)'
  };
}

export function checkIfPhone() {
  let check = false;
  (function (a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        a
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4)
      )
    )
      check = true;
  })(navigator.userAgent || navigator.vendor || window.opera);
  return check;
}

export function checkIfTabletDimensions() {
  let userAgent = navigator.userAgent.toLowerCase();
  return /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(
    userAgent
  );
}

export function hideElement(element) {
  setStyle(element, {
    opacity: 0
  });
}
export function showElement(element) {
  setStyle(element, {
    opacity: 1
  });
}
export function getRandomRgb() {
  let num = Math.round(0xffffff * Math.random());
  let r = num >> 16;
  let g = (num >> 8) & 255;
  let b = num & 255;

  return [r, g, b];
}
export function generateLighterColor(fg, o) {
  return fg
    .map((colFg, idx) => {
      return Math.round(o * colFg + (1 - o) * [255, 255, 255][idx]);
    })
    .join(',');
}

export function isTileMap(options) {
  return (
    options.series &&
    (options.series || []).some(function (s) {
      return s.type === 'tilemap';
    })
  );
}

export function rgbToHsv(rgbColor) {
  let r = rgbColor.r / 255.0;
  let g = rgbColor.g / 255.0;
  let b = rgbColor.b / 255.0;

  let cmax = Math.max(r, Math.max(g, b));
  let cmin = Math.min(r, Math.min(g, b));
  let diff = cmax - cmin;
  let h = -1,
    s = -1;

  if (cmax === cmin) h = 0;
  else if (cmax === r) h = (60 * ((g - b) / diff) + 360) % 360;
  else if (cmax === g) h = (60 * ((b - r) / diff) + 120) % 360;
  else if (cmax === b) h = (60 * ((r - g) / diff) + 240) % 360;

  if (cmax === 0) s = 0;
  else s = diff / cmax;

  let v = cmax;

  return { h, s, v };
}

/**
 * Converts HSV values to RGB
 * @param {number} H - hue
 * @param {number} S - saturation
 * @param {number} V - value
 * @returns {object} - object containing RGBA values
 */
export function hsvToRgb(H, S, V) {
  function calculate(n) {
    let k = (n + H / 60) % 6;
    return V - V * S * Math.max(0, Math.min(k, 4 - k, 1));
  }
  return {
    r: calculate(5) * 255,
    g: calculate(3) * 255,
    b: calculate(1) * 255,
    a: 255
  };
}

/**
 * Converts RGB object to a hex string
 *
 */
export function rgbObjToHex({ r, g, b }) {
  return rgbToHex(Math.round(r), Math.round(g), Math.round(b));
}

/**
 * Converts RGB object to a hex string
 *
 */
export function rgbaObjToHex({ r, g, b, a }) {
  return rgbaToHex(Math.round(r), Math.round(g), Math.round(b), Math.round(a));
}

/**
 * Converts RGB values to a hex string
 * @param {number} r - red
 * @param {number} g - green
 * @param {number} b - blue
 * @returns {string} hex string
 */
export function rgbToHex(r, g, b) {
  r = r.toString(16);
  g = g.toString(16);
  b = b.toString(16);

  if (r.length == 1) r = '0' + r;
  if (g.length == 1) g = '0' + g;
  if (b.length == 1) b = '0' + b;

  return '#' + r + g + b;
}

/**
 * Converts RGBA values to a hex string
 * @param {number} r - red
 * @param {number} g - green
 * @param {number} b - blue
 * @param {number} a - alpha
 * @returns {string} hex string
 */
export function rgbaToHex(r, g, b, a) {
  r = r.toString(16);
  g = g.toString(16);
  b = b.toString(16);
  a = a.toString(16);

  if (r.length == 1) r = '0' + r;
  if (g.length == 1) g = '0' + g;
  if (b.length == 1) b = '0' + b;
  if (a.length == 1) a = '0' + a;

  return '#' + r + g + b + a;
}

/**
 * Gets the color of a pixel in an ImageData array based on x/y coordinates
 * @param {array} imageData - ImageData from a canvas
 * @param {number} x - x coordinate
 * @param {number} y - y coordinate
 */
export function getPixel(imageData, x, y) {
  let pixelIndex = x * 4 + y * 4 * imageData.width;
  return {
    r: imageData.data[pixelIndex + 0],
    g: imageData.data[pixelIndex + 1],
    b: imageData.data[pixelIndex + 2],
    a: imageData.data[pixelIndex + 3]
  };
}

/**
 * Sets the color of a pixel in an ImageData array given x/y coordinates and color
 * @param {array} imageData - ImageData from a canvas
 * @param {number} x - x coordinate
 * @param {number} y - y coordinate
 * @param {object} color - a color object containing RGBA values
 */
export function setPixel(imageData, x, y, color) {
  let pixelIndex = x * 4 + y * 4 * imageData.width;
  imageData.data[pixelIndex + 0] = color.r;
  imageData.data[pixelIndex + 1] = color.g;
  imageData.data[pixelIndex + 2] = color.b;
  imageData.data[pixelIndex + 3] = color.a;
}

export function findAllIndexes(arr, val) {
  var indexes = [],
    i;
  for (i = 0; i < arr.length; i++) if (arr[i] === val) indexes.push(i);
  return indexes;
}

export async function asyncForEach(array, callback) {
  for (let index = 0; index < array.length; index++) {
    await callback(array[index], index, array);
  }
}

///////////////////////////////////////////////////////////////////////////

//Inject dependencies
ready(function () {
  if (!options.autoIncludeDependencies) {
    return false;
  }

  include('https://use.fontawesome.com/releases/v5.9.0/css/all.css');
  include('https://fonts.googleapis.com/css?family=Roboto:400,300,100,700|Source Sans:400,300,100', false, true);
});
